/**
 * Browser speech, kept in one place. Reading problems aloud uses the device's own voices.
 * Listening uses the browser's speech recognition, which in Chrome and Safari may send the audio to
 * Google or Apple to transcribe it. That's why it is opt-in, off by default, and says so where it is turned on.
 */
export interface VoicePrefs { speak: boolean; listen: boolean }
const KEY = "mathtrain.voice";

export function getVoicePrefs(): VoicePrefs {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "{}");
    return { speak: !!v.speak && canSpeak(), listen: !!v.listen && canListen() };
  } catch { return { speak: false, listen: false }; }
}
export function setVoicePrefs(p: VoicePrefs) {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* private mode */ }
}

export const canSpeak = () => typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
const Recognition = (): any => typeof window === "undefined" ? null : (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition ?? null;
export const canListen = () => !!Recognition();

/** Say `text`, replacing anything already being said. Calls `onEnd` once when it finishes, fails or is cut off. Returns a canceller. */
export function speak(text: string, onEnd?: () => void): () => void {
  if (!canSpeak()) { onEnd?.(); return () => {}; }
  const synth = window.speechSynthesis;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "en-US";
  u.rate = 0.95;
  let done = false;
  const finish = () => { if (!done) { done = true; onEnd?.(); } };
  u.onend = finish;
  u.onerror = finish;
  synth.speak(u);
  return () => { done = true; synth.cancel(); };
}

export const stopSpeaking = () => { if (canSpeak()) window.speechSynthesis.cancel(); };

export interface Listener { stop(): void }

/**
 * Listen for one utterance. `onHeard` gets the recognizer's best guesses, most likely first.
 * `onEnd` says why it stopped: "heard", "silence" (nothing said), "denied" (mic blocked) or "error".
 */
export function listen(onHeard: (alternatives: string[]) => void, onEnd: (why: "heard" | "silence" | "denied" | "error") => void): Listener {
  const R = Recognition();
  if (!R) { onEnd("error"); return { stop() {} }; }
  const rec = new R();
  rec.lang = "en-US";
  rec.continuous = false;
  rec.interimResults = false;
  rec.maxAlternatives = 4;
  let why: "heard" | "silence" | "denied" | "error" = "silence", stopped = false;
  rec.onresult = (e: any) => {
    const res = e.results[e.results.length - 1];
    const alts: string[] = [];
    for (let i = 0; i < res.length; i++) alts.push(res[i].transcript);
    why = "heard";
    if (!stopped) onHeard(alts);
  };
  rec.onerror = (e: any) => {
    if (e.error === "not-allowed" || e.error === "service-not-allowed") why = "denied";
    else if (e.error !== "no-speech" && e.error !== "aborted") why = "error";
  };
  rec.onend = () => { if (!stopped) onEnd(why); };
  try { rec.start(); } catch { onEnd("error"); }
  return { stop() { stopped = true; try { rec.abort(); } catch { /* already stopped */ } } };
}
